import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

export default function AddTaskModal({ onClose, onCreate, onSave }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('Please enter a task title');
      return;
    }

    setLoading(true);

    const taskData = {
      title: title.trim(),
      description: description.trim() || null,
      dueDate: dueDate || null,
      priority
    };

    const createHandler = typeof onCreate === 'function' ? onCreate : onSave;

    if (typeof createHandler !== 'function') {
      console.error('AddTaskModal: onCreate/onSave handler is not provided or not a function.');
      alert('Cannot create task right now. Please try again.');
      setLoading(false);
      return;
    }

    const result = await createHandler(taskData);

    if (result.success) {
      window.dispatchEvent(new CustomEvent('taskCreated'));
      onClose();
    } else {
      alert('Failed to create task: ' + (result.error || 'Unknown error'));
    }

    setLoading(false);
  };

  const handleQuickDueDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setDueDate(date.toISOString().split('T')[0]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">✏️ Add New Task</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Task Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What needs to be done? *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Follow up with ABC Corp about payment"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional notes (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Customer mentioned they need clarification on line item 3"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>


          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When is this due? (optional)
            </label>
            <div className="flex space-x-2 mb-3">
              <button
                type="button"
                onClick={() => handleQuickDueDate(0)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handleQuickDueDate(1)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => handleQuickDueDate(7)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                This Week
              </button>
              <button
                type="button"
                onClick={() => setDueDate('')}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                No Due Date
              </button>
            </div>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How urgent is this? (optional)
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setPriority('low')}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  priority === 'low'
                    ? 'border-green-400 bg-green-100 text-green-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Low
              </button>
              <button
                type="button"
                onClick={() => setPriority('medium')}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  priority === 'medium'
                    ? 'border-yellow-400 bg-yellow-100 text-yellow-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Medium
              </button>
              <button
                type="button"
                onClick={() => setPriority('high')}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  priority === 'high'
                    ? 'border-red-400 bg-red-100 text-red-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                High
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Smart tasks will auto-create for critical invoice actions.
              This is for your custom tasks.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

